/**
 * Which clip a body plays for each thing it can be doing.
 *
 * The engine knows a handful of things a body does: standing, walking, running,
 * swinging, falling over, stooping. What each of those looks like is content,
 * so a clip authored in Blender and dropped into the model pack can be pointed
 * at an action here rather than in the code.
 *
 * The clips offered are the ones the model actually carries, read off the body
 * the game has already loaded: a list typed by hand would be a list that goes
 * stale the first time somebody exports again.
 */

import { useEffect, useRef } from 'react';
import type { AdminCommand } from '../Admin';
import { Explain } from '../Explain';

/** What the engine can be told to play, and what each one is for. */
const POSES: { pose: string; label: string; about: string }[] = [
  { pose: 'idle', label: 'Idle', about: 'Standing still, which is most of the time.' },
  { pose: 'walk', label: 'Walk', about: 'Moving at a walk.' },
  { pose: 'run', label: 'Run', about: 'Moving at a run.' },
  { pose: 'hit', label: 'Attack', about: 'One swing. Plays once rather than looping.' },
  { pose: 'die', label: 'Death', about: 'Falling over, and staying down.' },
  { pose: 'pickup', label: 'Pick Up', about: 'Stooping to take something off the ground.' },
];

export interface PoseRow {
  pose: string;
  clip: string;
  loops: boolean;
  speed: number;
}

export function EditorPoses({
  written,
  clips,
  onCommand,
}: {
  /** What the world has been told, which is only the poses somebody has set. */
  written: readonly PoseRow[];
  /** Every clip the loaded model carries, by name. */
  clips: readonly string[];
  onCommand: AdminCommand;
}): JSX.Element {
  // Asked for once, when the panel opens. A ref rather than state, because
  // asking is not something the panel is drawn from.
  const asked = useRef(false);

  useEffect(() => {
    if (asked.current) return;

    asked.current = true;
    onCommand('listPoses');
  }, [onCommand]);

  const held = (pose: string): PoseRow | undefined => written.find((entry) => entry.pose === pose);

  const set = (pose: string, changed: Partial<PoseRow>): void => {
    const now = held(pose) ?? { pose, clip: '', loops: true, speed: 1 };
    const next = { ...now, ...changed };

    onCommand(
      'setPose',
      { loops: next.loops ? 1 : 0, speed: next.speed },
      { pose, clip: next.clip },
    );
  };

  return (
    <div className="editor-list">
      <p className="hint">
        A pose nothing is set for plays the clip the game was built with.
        <Explain>
          The clips offered are the ones the character model carries. Export a new one into the
          pack, reload, and it turns up here ready to be pointed at an action.
        </Explain>
      </p>

      {clips.length === 0 ? (
        <p className="hint">No model is loaded yet, so there are no clips to choose from.</p>
      ) : null}

      {POSES.map((entry) => {
        const now = held(entry.pose);

        return (
          <div className="editor-section" key={entry.pose}>
            <h4>{entry.label}</h4>

            <label className="editor-field">
              <span>
                Clip
                <Explain>{entry.about}</Explain>
              </span>
              <select
                value={now?.clip ?? ''}
                onChange={(event) => set(entry.pose, { clip: event.target.value })}
              >
                <option value="">Built In</option>
                {clips.map((clip) => (
                  <option key={clip} value={clip}>
                    {clip}
                  </option>
                ))}
              </select>
            </label>

            {now?.clip ? (
              <>
                <label className="editor-field">
                  <span>Speed</span>
                  <input
                    type="number"
                    min={0.1}
                    max={4}
                    step={0.1}
                    value={now.speed}
                    onChange={(event) =>
                      set(entry.pose, { speed: Number(event.target.value) || 1 })
                    }
                  />
                </label>

                <div className="editor-brushes">
                  <button
                    type="button"
                    className={now.loops ? 'editor-tab on' : 'editor-tab'}
                    onClick={() => set(entry.pose, { loops: !now.loops })}
                  >
                    {now.loops ? 'Loops' : 'Plays Once'}
                  </button>

                  <button
                    type="button"
                    className="editor-tab"
                    title="Back to the clip the game was built with"
                    onClick={() => set(entry.pose, { clip: '' })}
                  >
                    Reset
                  </button>
                </div>
              </>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
